import { useState, useCallback } from 'react';
import { getSingleGameDetailMobile } from '@/pages/intelligence/services/topcharts';
import { filterGameIdsByEntityType } from '@intelligence/v3/utils/topcharts';
import { dbiBtnLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import { buriedBtnPoint } from '@/pages/home/utils/buriedPoint';
import { getBasePath } from '@/utils/utils';

/**
 * Competitor game info from API
 */
export interface ICompetitorGame {
  app_id: string;
  name?: string;
  cover?: string;
  publisher?: string;
  entity_type?: string;
  [key: string]: any;
}

/**
 * Options for useGameComparison hook
 */
export interface IUseGameComparisonOptions {
  /** Current entity type: 'mobile' | 'pc' | 'console' | 'pc_console_combine' */
  entityType: string;
  /** Current game ID */
  gameId: string;
  /** All app IDs for the current game (used for mobile API call) */
  appIdsAll?: string[];
  /** Region type for API call */
  regionType?: string;
  /** Regions for API call */
  regions?: string[];
  /** Pre-loaded competitors from basicInfoOthers (for PC/Console) */
  preloadedCompetitors?: ICompetitorGame[];
}

/**
 * Return type for useGameComparison hook
 */
export interface IUseGameComparisonReturn {
  /** Whether competitors are currently being fetched */
  isLoading: boolean;
  /** Fetch competitors from API (for mobile) or use preloaded data */
  fetchCompetitors: () => Promise<ICompetitorGame[]>;
  /** Navigate to Compare page with current game + competitors */
  navigateToComparison: (source: 'tab' | 'vsButton') => Promise<void>;
  /** Get Compare URL parts based on entity type */
  getCompareUrlParts: () => { module: string; tab: string };
  /** Build the full Compare URL with games parameter */
  buildCompareUrl: (competitorIds: string[]) => string;
  /** Handle VS button click in competitor/similar games cards */
  handleVSClick: (localCompetitors: ICompetitorGame[]) => void;
}

/**
 * Custom hook for game comparison functionality
 *
 * Encapsulates:
 * - Compare page URL generation based on entity type
 * - Competitor fetching with caching (for mobile)
 * - Navigation logic with analytics tracking
 *
 * @example
 * // In main detail page
 * const { navigateToComparison } = useGameComparison({
 *   entityType,
 *   gameId,
 *   appIdsAll,
 *   regionType,
 *   regions,
 *   preloadedCompetitors: basicInfoOthers?.similar_apps,
 * });
 *
 * // In tab click handler
 * navigateToComparison('tab');
 *
 * @example
 * // In VS button component
 * const { handleVSClick } = useGameComparison({ entityType, gameId });
 * handleVSClick(localSimilarGames);
 */
const useGameComparison = (options: IUseGameComparisonOptions): IUseGameComparisonReturn => {
  const {
    entityType,
    gameId,
    appIdsAll = [],
    regionType = 'region',
    regions = ['global'],
    preloadedCompetitors = [],
  } = options;

  const basePath = getBasePath();

  const [isLoading, setIsLoading] = useState(false);

  /**
   * Get Compare page URL parts based on entity type
   * Mobile: Compare - Mobile Games - Active Users (DAU)
   * PC/Console: Compare - Multi-dimension (pcConsoleGamesBeta)
   */
  const getCompareUrlParts = useCallback((): { module: string; tab: string } => {
    if (entityType === 'mobile') {
      return { module: 'mobileGames', tab: 'dau' };
    }
    // PC/Console/Combine all use the same Compare page
    return { module: 'PcConsoleGamesBeta', tab: 'same_start_date' };
  }, [entityType]);

  /**
   * Build the full Compare URL with games parameter
   * @param competitorIds - Competitor IDs to include in the URL
   */
  const buildCompareUrl = useCallback((competitorIds: string[]): string => {
    const gameIds = gameId ? [gameId, ...competitorIds] : competitorIds;
    const filteredGameIds = filterGameIdsByEntityType(entityType, gameIds);
    const uniqueGameIds = [...new Set(filteredGameIds)];
    const gamesParam = uniqueGameIds.join(',');

    const { module: compareModule, tab: compareTab } = getCompareUrlParts();
    return `${basePath}/intelligence/compare/${compareModule}/${compareTab}?games=${gamesParam}`;
  }, [basePath, entityType, gameId, getCompareUrlParts]);

  /**
   * Fetch competitors from API (mobile) or return preloaded data (PC/Console)
   * API is called every time for mobile to get fresh data
   */
  const fetchCompetitors = useCallback(async (): Promise<ICompetitorGame[]> => {
    // For PC/Console, use preloaded competitors
    if (entityType !== 'mobile') {
      return preloadedCompetitors;
    }

    // Fetch from API for mobile (no caching)
    setIsLoading(true);
    try {
      const result = await getSingleGameDetailMobile({
        unified_id: gameId,
        app_ids: appIdsAll,
        entity_type: entityType,
        region_type: regionType,
        regions,
      });

      return result?.data?.similar_games || [];
    } catch (error) {
      console.error('Failed to fetch similar games:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [entityType, gameId, appIdsAll, regionType, regions, preloadedCompetitors]);

  /**
   * Navigate to Compare page with current game + competitors
   * Handles analytics tracking and different logic for mobile vs PC/Console
   *
   * @param source - Where the navigation was triggered from ('tab' | 'vsButton')
   */
  const navigateToComparison = useCallback(async (source: 'tab' | 'vsButton'): Promise<void> => {
    // Track analytics
    const logKey = source === 'tab' ? 'gameComparisonTabClick' : 'gameComparisonVSButtonClick';
    const logConfig = dbiBtnLogMes[logKey];
    if (logConfig) {
      buriedBtnPoint(logConfig);
    }

    let competitorIds: string[] = [];

    if (entityType === 'mobile') {
      // Mobile: fetch competitors on demand (with caching)
      const fetchedCompetitors = await fetchCompetitors();
      // Mobile limit: first 5 competitors (max 6 total with main game)
      competitorIds = fetchedCompetitors
        .slice(0, 5)
        .map((item) => item.app_id)
        .filter(Boolean);
    } else {
      // PC/Console: use preloaded competitors
      // PC/Console limit: first 12 competitors (max 13 total with main game)
      competitorIds = preloadedCompetitors
        .slice(0, 12)
        .map((item) => item.app_id)
        .filter(Boolean);
    }

    const url = buildCompareUrl(competitorIds);
    window.open(url, '_blank');
  }, [entityType, fetchCompetitors, preloadedCompetitors, buildCompareUrl]);

  /**
   * Handle VS button click in competitor/similar games cards
   * Uses locally available competitors (already rendered in the card list)
   *
   * @param localCompetitors - Competitors from the local component state
   */
  const handleVSClick = useCallback((localCompetitors: ICompetitorGame[]): void => {
    // Track analytics
    const logConfig = dbiBtnLogMes['gameComparisonVSButtonClick'];
    if (logConfig) {
      buriedBtnPoint(logConfig);
    }

    const competitorIds = localCompetitors
      .map((item) => item.app_id)
      .filter(Boolean);

    if (competitorIds.length === 0 && !gameId) {
      return;
    }

    const url = buildCompareUrl(competitorIds);
    window.open(url, '_blank');
  }, [gameId, buildCompareUrl]);

  return {
    isLoading,
    fetchCompetitors,
    navigateToComparison,
    getCompareUrlParts,
    buildCompareUrl,
    handleVSClick,
  };
};

export default useGameComparison;
